/**
 * Sortable Work — drag-to-reorder for the action-plan task lists.
 *
 * Modeled on ~/loop's sortable-strategy.js. Every `[data-sortable-work]`
 * container holds one sibling group and becomes its own Sortable instance with
 * `pull`/`put` off, so a drag can never move a row out of its group — a
 * subtask cannot land under a different parent, and a top-level task cannot
 * land inside a subtask list. The server re-checks the same thing.
 *
 * Container attrs:
 *   data-sortable-work   marks the container
 *   data-sortable-model  what the endpoint is being asked to reorder ('task')
 *   data-sortable-url    the reorder endpoint (work:task-reorder)
 *
 * Row attrs:
 *   data-sortable-id        the row's pk
 *   data-sortable-children  on the `.collapse` holding a parent's subtasks,
 *                           set to that parent's pk. The SeeMe tree shape makes
 *                           that block a SIBLING of its parent row rather than
 *                           a child, so dragging the row would otherwise leave
 *                           its subtasks behind — `reflowChildren` walks them
 *                           back into place after every drop.
 */
import Sortable from 'sortablejs';

(function () {
    var instances = [];

    function reflowChildren(container) {
        container.querySelectorAll(':scope > [data-sortable-id]').forEach(function (row) {
            var id = row.getAttribute('data-sortable-id');
            var children = container.querySelector(':scope > [data-sortable-children="' + id + '"]');
            if (children) row.after(children);
        });
    }

    function postOrder(container) {
        var url = container.getAttribute('data-sortable-url');
        if (!url) return;

        var body = new FormData();
        body.append('model', container.getAttribute('data-sortable-model'));
        container.querySelectorAll(':scope > [data-sortable-id]').forEach(function (row) {
            body.append('ids[]', row.getAttribute('data-sortable-id'));
        });

        var xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('X-CSRFToken', window._cc.getCsrfToken());
        // Without this `permission_required` renders its no-access PAGE at
        // status 200, so a refused drag would report success. With it the
        // denial is a real 403 and the error toast below fires.
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.onload = function () {
            if (xhr.status < 400) {
                // Most sortable lists only need the optimistic DOM order. Stage
                // lists also name a server-rendered card or editor that must
                // refresh, so honor those explicit HX events without turning
                // this XMLHttpRequest into a second htmx transport.
                var trigger = xhr.getResponseHeader('HX-Trigger');
                if (trigger && window.htmx) {
                    try {
                        var events = JSON.parse(trigger);
                        Object.keys(events).forEach(function (name) {
                            if (name !== 'showToast') htmx.trigger(document.body, name, events[name]);
                        });
                    } catch (e) { /* A malformed optional trigger cannot undo the saved order. */ }
                }
                if (window.showToast) window.showToast('Order saved', 'success');
            } else if (window.showToast) {
                window.showToast('Could not save the new order', 'error');
            }
        };
        xhr.onerror = function () {
            if (window.showToast) window.showToast('Could not save the new order', 'error');
        };
        xhr.send(body);
    }

    function destroySortable() {
        instances.forEach(function (instance) {
            try { instance.destroy(); } catch (e) { /* container already gone */ }
        });
        instances = [];
    }

    function initSortable() {
        destroySortable();
        document.querySelectorAll('[data-sortable-work]').forEach(function (container) {
            instances.push(Sortable.create(container, {
                // The handle is the only drag target, so the row's stretched
                // name link and its status dropdown still work on a click.
                handle: '.drag-handle',
                draggable: '[data-sortable-id]',
                group: { name: 'work-task', pull: false, put: false },
                animation: 150,
                ghostClass: 'sortable-ghost',
                chosenClass: 'sortable-chosen',
                fallbackOnBody: true,
                swapThreshold: 0.65,
                onEnd: function (evt) {
                    reflowChildren(evt.to);
                    postOrder(evt.to);
                }
            }));
        });
    }

    function boot() { initSortable(); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();

    // A boosted navigation or a card swap brings in fresh containers, which
    // carry no Sortable instance. A row swapped in place after a transition
    // does not — it is not a container and holds none — so the guard keeps a
    // status change from re-initialising the whole page.
    document.body.addEventListener('htmx:afterSettle', function (evt) {
        if (evt.target.querySelector && evt.target.querySelector('[data-sortable-work]')) {
            initSortable();
        }
    });
    // `sheets.js` fills a sheet body with innerHTML, so its task list arrives
    // outside any htmx swap.
    document.body.addEventListener('sheet:ready', initSortable);
})();
